import Vue from 'vue'
import { uploadFile } from '@/helpers/file.helpers'

const client = () => Vue.prototype.$api

const TOKEN_QUERY_PARAM = 't'
const PATIENT_ID_QUERY_PARAM = 'patientId'
const PET_ID_QUERY_PARAM = 'petId'
const REQUIRED_PARAMS = [TOKEN_QUERY_PARAM, PATIENT_ID_QUERY_PARAM, PET_ID_QUERY_PARAM]

function getRequiredQueryParams () {
	const queryString = window.location.search
	const urlParams = new URLSearchParams(queryString)
	// Return key value object with required query params
	return REQUIRED_PARAMS.reduce((acc, param) => {
		acc[param] = urlParams.get(param)
		return acc
	}, {})
}

export default function () {
		const { t, patientId, petId } = getRequiredQueryParams()
    if (t) {
        Vue.prototype.$readOnlyToken = t
    }
    return {
        // Variables
        getAllVariables () {
            return client().get('/history/variables', { params: { patientId: patientId, petId: petId } })
        },
        getVariableValues (name) {
            return client().get(`/history/variables/${name}`, { params: { patientId: patientId, petId: petId } })
        },
        saveVariableValue (data) {
            includeParamsToBody(data)
            return client().post('/history/variables', data)
        },
        updateActiveVars (vars) {
            return client().put('/history/variables/updateActiveVars', null, { params: { patientId: patientId, petId: petId, activeVars: vars } })
        },
        saveVariableTracking (data) {
            includeParamsToBody(data)
            return client().post('/history/variables/tracking', data)
        },
        getVariableTracking (varName) {
            return client().get('/history/variables/tracking', { params: { varName: varName,  patientId: patientId, petId: petId } })
        },
        removeVariableTracking (varName) {
            return client().delete('/history/variables/tracking', { params: { varName: varName,  patientId: patientId, petId: petId } })
        },
        // Appointments
        getAppointments (specialtyId = null) { 
            return client().get('/history/appointments', { params: { specialtyId: specialtyId, patientId: patientId, petId: petId} })
        },
        // Observations
        getObservations () {
            return client().get('/history/observations', { params: { patientId: patientId, petId: petId} })
        },
        saveObservations (data) {
            includeParamsToBody(data)
            return client().post(`/history/observations`, data)
        },
        // Events
        getEvents () {
            return client().get('/history/events', { params: { patientId: patientId } })
        },
        saveEvents (data) {
            includeParamsToBody(data)
            return client().post(`/history/events`, data)
        },
        // Allergies
        getHistoryAllergies () {
            return client().get('/history/allergies', { params: { patientId: patientId, petId: petId } })
        },
        getAllergiesByName (name) {
            return client().get('allergies', { params: { name: name } })
        },
        saveHistoryAllergy (data) {
            includeParamsToBody(data)
            return client().post('/history/allergies', data)
        },
        deleteHistoryAllergy (id) {
            return client().delete('/history/allergies', { params: { historyAllergyId: id, patientId: patientId } })
        },
        getSeverities () {
            return client().get('severities')
        },
        // Medication Allergies
        getHistoryMedAllergies () {
            return client().get('/history/medallergies', { params: { patientId: patientId, petId: petId } })
        },
        getMedAllergiesByName (name) {
            return client().get('/medicines/allergies', { params: { name: name } })
        },
        saveHistoryMedAllergies (data) {
            includeParamsToBody(data)
            return client().post('/history/medallergies', data)
        },
        deleteHistoryMedAllergy (id) {
            return client().delete('/history/medallergies', { params: { historyAllergyId: id, patientId: patientId } })
        },
        // Diseases
        getHistoryDiseases () {
            return client().get('/history/diseases', { params: { patientId: patientId, petId: petId } })
        },
        saveHistoryDisease (data) {
            includeParamsToBody(data)
            return client().post('history/diseases', data)
        },
        deleteHistoryDisease (disease) {
            return client().delete('history/diseases', { params: { historyDiseaseId: disease, patientId: patientId } })
        },
        // Chronic diseases
        getHistoryChrondiseases () {
            return client().get('/history/chrondiseases', { params: { patientId: patientId } })
        },
        getChronDiseasesByName (name) {
            return client().get('chronicdiseases', { params: { name: name } })
        },
        saveHistoryChronDisease (data) {
            includeParamsToBody(data)
            return client().post('history/chrondiseases', data)
        },
        deleteHistoryChronDisease (disease) {
            return client().delete('history/chrondiseases', { params: { historyDiseaseId: disease, patientId: patientId } })
        },
        // Medications
        getHistoryMedications ({ past }) {
            return client().get('/history/medications', { params: { patientId: patientId, petId: petId, past } })
        },
        getMedicinesByName (name) {
            return client().get('medicines', { params: { name: name } })
        },
        getMedicinesById (id) {
            return client().get('medicines', { params: { id: id } })
        },
        saveHistoryMedication (data) {
            includeParamsToBody(data)
            return client().post('/history/medications', data)
        },
        deleteHistoryMedication (historyMedicationId) {
            return client().delete('history/medications', { params: { historyMedicationId: historyMedicationId, patientId: patientId } })
        },
        stopHistoryMedication (data) {
            includeParamsToBody(data)
            return client().post('history/medications/stop', data)
        },
        getMedicationReminder (id) {
            return client().get('history/medications/reminder', { params: { id: id, patientId: patientId } })
        },
        saveMedicationReminder (data) {
            includeParamsToBody(data)
            return client().post('history/medications/reminder', data)
        },
        deleteMedicationReminder (id) {
            return client().delete('history/medications/reminder', { params: { id: id, patientId: patientId } })
        },
        // Vaccinations
        getHistoryVaccinations () {
            return client().get('/history/vaccinations', { params: { patientId: patientId, petId: petId } })
        },
        getAllVaccines () {
            return client().get('vaccines')
        },
        getVaccineLabs (vaccineId) {
            return client().get('vaccines/labs', { params: { vaccineId: vaccineId }})
        },
        saveHistoryVaccination (data) {
            includeParamsToBody(data)
            return client().post('/history/vaccinations', data)
        },
        deleteHistoryVaccination (disease) {
            return client().delete('history/vaccinations', { params: { historyVaccinationId: disease, patientId: patientId } })
        },
        // Family backgrounds
        getHistoryFamilyBackgrounds () {
            return client().get('/history/backgrounds', { params: { patientId: patientId } })
        },
        saveHistoryFamilyBackgrounds (data) {
            includeParamsToBody(data)
            return client().post('/history/backgrounds', data)
        },
        deleteHistoryFamilyBackgrounds (id) {
            return client().delete('history/backgrounds', { params: { historyFamilyBackgroundId: id, patientId: patientId } })
        },
        getHistoryFamilyRelationships () {
            return client().get('familyRelationships')
        },
        // Files
        getHistoryFiles () {
            return client().get('/history/files', { params: { patientId: patientId, petId: petId } })
        },
        deleteHistoryFile (id) {
            return client().delete('history/files', { params: { id: id, patientId: patientId } })
        },
        getFiles (fileId) {
          return client().get('/files/urlAuth', { params: { id: fileId, patientId: patientId } })
        },
        lockFile (id) {
            return client().put('/history/files/lock', null, { params: {id: id} })
        },
        unlockFile (id) {
            return client().put('/history/files/unlock', null, { params: {id: id} })
        },
         // Blood
        getBloodTypes () {
            return client().get('/v1/BloodTypes')
        },
        // Habits
        getHabits () {
            return client().get('/history/habits', { params: { patientId: patientId } })
        },
        saveHabits (data) {
            includeParamsToBody(data)
            return client().post('/history/habits', data)
        },
        getAllHabits () {
            return client().get('/habits', { params: { patientId: patientId } })
        },
        // Moods
        getAllMoods () {
            return client().get('/moods')
        },
        getHistoryMoods (lastMonths = null) {
            return client().get('/history/moods', { params: { patientId: patientId, lastMonths:lastMonths } })
        },
        saveMood (data) {
            includeParamsToBody(data)
            return client().post('/history/moods', data)
        },
        getAllMoods () {
            return client().get('/moods', { params: { patientId: patientId } })
        },
        // Healthy habits
        getHealthyHabits () {
            return client().get('/history/healthyHabits', { params: { patientId: patientId } })
        },
        // Quiz
        getQuiz (quizType) {
            return client().get('/history/quiz', { params: { quizType: quizType, patientId: patientId } })
        },
        getCompletedQuizIds () {
            return client().get('/history/quiz/completed', { params: { patientId: patientId } })
        },
        saveQuiz (quizData) {
            return client().post('/history/quiz', quizData)
        },
        // Apps
        getApps () {
            return client().get('/history/apps', { params: { patientId: patientId } })
        },
        saveApps (data) {
            includeParamsToBody(data)
            return client().post('/history/apps', data)
        },
        deleteApps (id) {
            return client().delete('history/apps', { params: { appId: id, patientId: patientId } })
        },
        // ProfessionalsAndCentersSharedClinic
        getProfsAndCentersGrantAccessHistory (name) {
            return client().get('history/profsAndCentersGrantAccessHistory', { params: { name: name } })
        },
        getProfsAndCentersGrantAccessHistoryById (id) {
            return client().get(`history/profsAndCentersGrantAccessHistory/${id}`)
        },
        grantAccessHistory (data) {
            return client().post('history/profsAndCentersGrantAccessHistory', data)
        },
        shareHistoryLink (data) {
            return client().post('history/history-link', data)
        },
        // GrantAccessClinicHistory
        getProfessionalsAndCentersSharedClinic () {
            return client().get('history/professionalsAndCentersSharedClinic')
        },
        deleteProfessionalsAndCentersSharedClinic (id) {
            return client().delete('history/professionalsAndCentersSharedClinic', { params: { idDoctorCenter: id } })
        },
        // Chart
        getHistoryChart (varName) {
            return client().get('history/chart', { params: { varName: varName, patientId: patientId, petId: petId } })
        },
        // Documents
        markFileAsCompleted (data) {
            includeParamsToBody(data)
            return client().post('history/files/markAsCompleted', data)
        },
        uploadFileToClinicHistory: async function (file, fileTagId) {
            await uploadFile('PatientsClinicalDocument', {id: patientId, petId: petId, idFileTag: fileTagId}, file, true)
        },
        overwriteFileClinicHistory: async function (file, fileId) {
            await uploadFile('history/files/markAsCompleted', {id: fileId, patientId: patientId, petId: petId}, file)
        }
    }

    function includeParamsToBody (data) {
        if (patientId && !data.patientId) {
            data.patientId = patientId
        }
        if (petId && !data.petId) {
            data.petId = petId
        }
    }
}



// WEBPACK FOOTER //
// ./src/repositories/ClinicHistoryRepository.js